import { useCallback, useEffect, useRef, useState } from 'react'
import { getUnseenMarketplaceDemands, type UnseenMarketplaceDemands } from '../api'

/**
 * How often the unseen count is refreshed while the dashboard is open and the tab visible.
 * The indicator is ambient information, so this is deliberately slow — it exists so a SIB
 * who leaves the dashboard open still notices new listings, not to be real-time.
 */
export const UNSEEN_POLL_INTERVAL_MS = 60_000

const EMPTY: UnseenMarketplaceDemands = { hasUnseen: false, count: 0, unseenDemandIds: [] }

export type UseUnseenMarketplaceDemands = UnseenMarketplaceDemands & {
  refresh: () => Promise<void>
}

/**
 * Polls the unseen-demand count for the marketplace navigation indicator.
 *
 * Failures are swallowed on purpose: this drives a decorative dot, and a flaky poll must
 * never produce an error state or a toast on the dashboard. The last known value is kept,
 * so a transient failure leaves the badge as it was rather than clearing it.
 *
 * Polling pauses while the tab is hidden and refreshes immediately when it becomes visible
 * again, so a backgrounded dashboard neither wastes requests nor shows a stale count.
 */
export function useUnseenMarketplaceDemands(enabled = true): UseUnseenMarketplaceDemands {
  const [unseen, setUnseen] = useState<UnseenMarketplaceDemands>(EMPTY)
  const mountedRef = useRef(true)

  const refresh = useCallback(async () => {
    try {
      const result = await getUnseenMarketplaceDemands()
      if (!mountedRef.current) return
      setUnseen(result)
    } catch (error) {
      console.warn('Failed to load unseen marketplace demands:', error)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true

    if (!enabled) {
      return () => {
        mountedRef.current = false
      }
    }

    void refresh()

    const intervalId = window.setInterval(() => {
      // Skip the tick rather than clearing the interval: the tab may become visible again
      // at any point and we want the cadence to resume without re-arming.
      if (document.visibilityState === 'hidden') return
      void refresh()
    }, UNSEEN_POLL_INTERVAL_MS)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      mountedRef.current = false
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, refresh])

  return { ...unseen, refresh }
}
